import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Switch } from '@/components/ds';
import {
  ALERT_CHANNELS,
  fetchAlertPreferences,
  PUSH_SWITCHES,
  setAlertPreference,
  type AlertChannel,
  type PushPreferences,
  type PushSwitch,
} from '../../lib/notificationPreferences';

type Loaded = Partial<Record<AlertChannel, PushPreferences>>;

/**
 * What reaches a person outside the app, per channel (phone, email, Slack),
 * read by push-notify before it sends (migrations 1009 and 1059). Kept on the
 * server, so the phone and this page show one answer.
 */
export function PhoneAlertPreferences({ onClose }: { onClose: () => void }) {
  const [preferences, setPreferences] = useState<Loaded>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all(ALERT_CHANNELS.map((c) => fetchAlertPreferences(c.key).then((r) => [c.key, r] as const))).then(
      (results) => {
        if (cancelled) return;
        const next: Loaded = {};
        let firstError: string | null = null;
        for (const [key, r] of results) {
          if (r.preferences) next[key] = r.preferences;
          else firstError ??= r.error ?? 'unknown error';
        }
        setPreferences(next);
        setError(firstError ? `Some switches could not be read: ${firstError}` : null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = async (channel: AlertChannel, key: PushSwitch, on: boolean) => {
    setError(null);
    const previous = preferences[channel];
    setPreferences((current) => (current[channel] ? { ...current, [channel]: { ...current[channel]!, [key]: on } } : current));
    const result = await setAlertPreference(channel, key, on);
    if (result.preferences) {
      setPreferences((current) => ({ ...current, [channel]: result.preferences! }));
    } else {
      setPreferences((current) => ({ ...current, [channel]: previous }));
      setError(`That switch was not saved: ${result.error ?? 'unknown error'}`);
    }
  };

  return (
    <div className="bg-[var(--bg-raised)] border p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-[var(--text-pri)]">Alerts</h2>
          <p className="text-sm text-[var(--text-ter)] mt-0.5">
            What reaches you outside the app. Every notification stays here either way.
          </p>
        </div>
        <button
          onClick={onClose}
          aria-label="Close alerts"
          className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {error && <p className="text-sm text-[var(--text-sec)]">{error}</p>}
      <div className="grid gap-6 md:grid-cols-3">
        {ALERT_CHANNELS.map((channel) => {
          const on = preferences[channel.key];
          if (!on) return null;
          return (
            <section key={channel.key} className="space-y-3" aria-label={`${channel.label} alerts`}>
              <div>
                <h3 className="text-sm font-semibold text-[var(--text-pri)]">{channel.label}</h3>
                <p className="text-xs text-[var(--text-ter)]">{channel.detail}</p>
              </div>
              {PUSH_SWITCHES.map((row) => (
                <Switch
                  key={row.key}
                  checked={on[row.key]}
                  onChange={(value) => void toggle(channel.key, row.key, value)}
                  label={row.label}
                  description={row.detail}
                />
              ))}
            </section>
          );
        })}
      </div>
      <p className="text-xs text-[var(--text-ter)]">Agent summaries stay in the app and never reach your phone, email or Slack.</p>
    </div>
  );
}
